import { Box, Grid } from "@mui/material";
import { scrollToAnchor } from "../../theme/commonStyles";
import ContentBox from "../ContentBox/ContentBox";
import { FormContainer, SelectElement, TextFieldElement, useForm } from "react-hook-form-mui";
import ButtonLoading from "../Button/ButtonLoading";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import _ from "lodash";
import { userHasOneOfTheseRoles, userHasRole } from "../../services/backofficeUserService";
import { Roles } from "../../types/BackofficeUser";
import ChangePassword from "./ChangePassword";
import React, { useEffect, useState } from "react";
import { apiPost } from "../../services/Api/apiCall";
import EmailChangeModal from "../EmailChangeModal/EmailChangeModal";
import { fetchCollection, queryKeys, updateResource } from "../../services/ReactQuery/reactQueryService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmail } from "../../services/validationRules";
import LegalbirdLoader from "../ContentLoader/LegalbirdLoader";
import { translate } from "../../services/Translations/translatorService";
import MenuItem from "@mui/material/MenuItem";
import CustomSelectElement from "../ReactHookFormElements/SelectElement";
import ReactHookFormDevTools from "../ReactHookFormDevTools/ReactHookFormDevTools";

export default function PersonalData() {
  const [isEmailModalOpen, setIsEmailModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const currentUser = useCurrentUser();
  const updateMutation = useMutation({
    mutationFn: updateResource
  });
  const queryClient = useQueryClient();
  const externalUserFilter = { backofficeUser: currentUser.id };
  const { data: externalUserCollection, isFetching: isFetchingExternalUser } = useQuery({
    queryKey: queryKeys.collection("external_users", externalUserFilter),
    queryFn: () => fetchCollection("external_users", externalUserFilter),
    enabled: currentUser.isExternal,
  });
  const externalUser =
    externalUserCollection && externalUserCollection["hydra:totalItems"] > 0
      ? externalUserCollection["hydra:member"][0]
      : null;

  const { data: externalUserHumanResourceCollection, isFetching: isFetchingExternalUserHumanResource } = useQuery({
    queryKey: queryKeys.collection("human_resources", externalUserFilter),
    queryFn: () => fetchCollection("human_resources", externalUserFilter),
    enabled: userHasRole(currentUser, Roles.external),
  });

  const externalUserHumanResource =
    externalUserHumanResourceCollection && externalUserHumanResourceCollection["hydra:totalItems"] > 0
      ? externalUserHumanResourceCollection["hydra:member"][0]
      : null;

  const handleSubmit = async (values: any) => {
    setIsLoading(true);
    if (values.email !== currentUser.email) {
      const emailCheck = await apiPost("check_user_email", { email: _.toLower(values.email) });
      if (emailCheck.customerExists) {
        setIsLoading(false);
        alert(emailCheck.errorText || "Es existiert bereits ein Konto mit dieser E-Mail-Adresse");
        return;
      }
      setIsEmailModalOpen(true);
    }
    const personData: Record<string, any> = {
      gender: values.person.gender,
      givenName: values.person.givenName,
      familyName: values.person.familyName,
      nameAddition: values.person.nameAddition,
      telephone: values.person.telephone,
      titles: _.filter(values.person.titles, (titleData) => {
        return titleData && titleData.title && !["no_title", ""].includes(titleData.title);
      }),
    };
    await updateMutation.mutateAsync(
      {
        uri: "backoffice_people",
        id: currentUser.person.id,
        data: personData,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: queryKeys.all("backoffice_users")
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.all("current_user")
          });
        },
      }
    );

    if (userHasRole(currentUser, Roles.external)) {
      await updateMutation.mutateAsync(
        {
          uri: "human_resources",
          id: externalUserHumanResource.id,
          data: { trainedProducts: values.externalUser.trainedProducts },
        },
        {
          onSuccess: () => queryClient.invalidateQueries({
            queryKey: queryKeys.collection("human_resources", externalUserFilter)
          }),
        }
      );
    }

    if (externalUser?.organization?.id) {
      await updateMutation.mutateAsync(
        {
          uri: "backoffice_organizations",
          id: externalUser.organization.id,
          data: {
            name: values.externalUser.organization.name,
          },
        },
        {
          onSuccess: () => queryClient.invalidateQueries({
            queryKey: queryKeys.collection("external_users", externalUserFilter)
          }),
        }
      );
    }

    setIsLoading(false);
  };

  const getInitialTitles = (titles: any) => {
    if (!titles) {
      return null;
    }

    if (titles.length < 1) {
      return [{ title: "no_title", product: "all", fullTitle: "Kein Titel" }];
    }

    return titles;
  };

  const formContext = useForm({
    defaultValues: {
      email: currentUser.email,
      person: {
        gender: currentUser.person.gender,
        givenName: currentUser.person.givenName,
        familyName: currentUser.person.familyName,
        nameAddition: currentUser.person.nameAddition,
        telephone: currentUser.person.telephone,
        titles: getInitialTitles(currentUser.person.titles),
      },
      externalUser: {
        organization: {
          name: null,
        },
        trainedProducts: [],
      },
    },
  });

  useEffect(() => {
    if (externalUserCollection) {
      formContext.setValue("externalUser.organization.name", externalUserCollection["hydra:member"][0]?.organization?.name);
    }
  }, [externalUserCollection, formContext]);

  useEffect(() => {
    if (externalUserHumanResourceCollection) {
      formContext.setValue("externalUser.trainedProducts", externalUserHumanResourceCollection["hydra:member"][0]?.trainedProducts || []);
    }
  }, [externalUserHumanResourceCollection, formContext]);

  if (isFetchingExternalUser || isFetchingExternalUserHumanResource) {
    return <LegalbirdLoader />;
  }

  const { isDirty } = formContext.formState;

  return (
    <>
      <Box sx={scrollToAnchor} id={"myData"} />
      <ContentBox headline={"Persönliche Daten"}>
        <FormContainer formContext={formContext} onSuccess={handleSubmit}>
          <Grid container spacing={3}>
            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <SelectElement
                name={"person.gender"}
                label={"Anrede"}
                options={[
                  { id: "male", label: "Herr" },
                  { id: "female", label: "Frau" },
                ]}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <TextFieldElement
                name={"person.givenName"}
                label={"Vorname"}
                rules={{
                  required: "Vorname ist ein Pflichtfeld",
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <TextFieldElement
                name={"person.familyName"}
                label={"Nachname"}
                rules={{
                  required: "Nachname ist ein Pflichtfeld",
                }}
              />
            </Grid>
            {userHasRole(currentUser, Roles.lawyer) && (
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <TextFieldElement name={"person.nameAddition"} label={"Namenszusatz"} />
              </Grid>
            )}
            {userHasRole(currentUser, Roles.external) && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <TextFieldElement name={"externalUser.organization.name"} label={"Kanzleiname"} />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <TextFieldElement
                    name={"person.telephone"}
                    label={"Telefon"}
                    rules={{
                      required: "Telefon ist ein Pflichtfeld",
                      pattern: {
                        value: /^0?[1-9]\d+$/,
                        message:
                          "Die Telefonnummer darf keine Sonderzeichen enthalten oder mehrere führende Nullen haben.",
                      },
                    }}
                  />
                </Grid>
              </>
            )}
            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <TextFieldElement
                name={"email"}
                label={"E-Mail"}
                rules={{
                  required: "E-Mail ist ein Pflichtfeld",
                  validate: { isEmail: (value: string) => isEmail(value) || "Dies ist keine gültige E-Mail Adresse" },
                }}
                slotProps={{
                  input: {
                    readOnly: !userHasRole(currentUser, Roles.external),
                  },
                }}
              />
            </Grid>
            {userHasRole(currentUser, Roles.external) && (
              <Grid size={12}>
                <CustomSelectElement label={"Rechtsprodukte"} name={"externalUser.trainedProducts"} multiple>
                  <MenuItem value={"divorce"}>{translate("divorce.label")}</MenuItem>
                  <MenuItem value={"familyLawCase"}>{translate("familyLawCase.label")}</MenuItem>
                  <MenuItem value={"alimony"}>{translate("alimony.label")}</MenuItem>
                  <MenuItem value={"death"}>{translate("death.label")}</MenuItem>
                  <MenuItem value={"traffic"}>{translate("traffic.label")}</MenuItem>
                  <MenuItem value={"trafficAccident"}>{translate("trafficAccident.label")}</MenuItem>
                  <MenuItem value={"settlement"}>{translate("settlement.label")}</MenuItem>
                  <MenuItem value={"contractLaw"}>{translate("general.productCategory.contractLaw")}</MenuItem>
                  <MenuItem value={"dataLeakContract"}>{translate("dataLeakContract.label")}</MenuItem>
                  <MenuItem value={"rentalContract"}>{translate("rentalContract.label")}</MenuItem>
                  <MenuItem value={"civilServiceLaw"}>{translate("civilServiceLaw.label")}</MenuItem>
                </CustomSelectElement>
              </Grid>
            )}
            <Grid size={12}>
              {userHasOneOfTheseRoles(currentUser, [Roles.lawyer]) && <LawyerTitles />}
              {userHasOneOfTheseRoles(currentUser, [Roles.accountmanager]) && <AccountManagerTitles />}
            </Grid>
            <Grid size={12}>
              <ButtonLoading
                isLoading={isLoading}
                sx={{ width: "200px", margin: "0 auto", display: "block" }}
                variant={"contained"}
                disabled={!isDirty}
                type={"submit"}
              >
                Speichern
              </ButtonLoading>
            </Grid>
          </Grid>
          <Box sx={{ marginTop: "2rem" }}>
            <ChangePassword />
          </Box>
          <EmailChangeModal setIsEmailModalOpen={setIsEmailModalOpen} isEmailModalOpen={isEmailModalOpen} />
        </FormContainer>
        <ReactHookFormDevTools control={formContext.control} />
      </ContentBox>
    </>
  );
}

const LawyerTitles = () => {
  const currentUser = useCurrentUser();
  return (
    <>
      <SelectElement
        name={"person.titles.0.title"}
        label={"Titel"}
        options={[
          {
            id: currentUser.person.gender === "male" ? "Rechtsanwalt" : "Rechtsanwältin",
            label: currentUser.person.gender === "male" ? "Rechtsanwalt" : "Rechtsanwältin",
          },
          {
            id: currentUser.person.gender === "male" ? "Assessor" : "Assessorin",
            label: currentUser.person.gender === "male" ? "Assessor" : "Assessorin",
          },
          {
            id: currentUser.person.gender === "male" ? "Diplomjurist" : "Diplomjuristin",
            label: currentUser.person.gender === "male" ? "Diplomjurist" : "Diplomjuristin",
          },
        ]}
      />
      {_.map(_.slice(currentUser.person.titles, 1), (titleData, index) => {
        return (
          <SelectElement
            key={index + 1}
            label={"Fachanwaltstitel"}
            name={`person.titles.${index + 1}.title`}
            options={getSpecialListLawyerTitleOptions()}
          />
        );
      })}
      <SelectElement
        key={currentUser.person.titles!.length}
        label={"Weiterer Fachanwaltstitel"}
        name={`person.titles.${currentUser.person.titles!.length}.title`}
        options={getSpecialListLawyerTitleOptions()}
      />
    </>
  );
};

const AccountManagerTitles = () => {
  const currentUser = useCurrentUser();

  if (!currentUser.person.titles) {
    return null;
  }

  return (
    <SelectElement
      name={"person.titles.0.title"}
      label={"Titel"}
      options={[
        {
          id: "no_title",
          label: "Kein Titel",
        },
        {
          id: currentUser.person.gender === "male" ? "Rechtsanwaltsfachangestellter" : "Rechtsanwaltsfachangestellte",
          label:
            currentUser.person.gender === "male" ? "Rechtsanwaltsfachangestellter" : "Rechtsanwaltsfachangestellte",
        },
        {
          id: currentUser.person.gender === "male" ? "Rechtsfachwirt" : "Rechtsfachwirtin",
          label: currentUser.person.gender === "male" ? "Rechtsfachwirt" : "Rechtsfachwirtin",
        },
        {
          id: currentUser.person.gender === "male" ? "Auszubildender" : "Auszubildende",
          label: currentUser.person.gender === "male" ? "Auszubildender" : "Auszubildende",
        },
      ]}
    />
  );
};

const getSpecialListLawyerTitleOptions = () => {
  const specialistLawyerTitles = [
    "Familienrecht",
    "Verkehrsrecht",
    "Arbeitsrecht",
    "Erbrecht",
    "Miet- und Wohnungseigentumsrecht",
    "Verwaltungsrecht",
    "Steuerrecht",
    "Sozialrecht",
    "Strafrecht",
    "Insolvenz- und Sanierungsrecht",
    "Versicherungsrecht",
    "Medizinrecht",
    "Bau- und Architektenrecht",
    "Transport- und Speditionsrecht",
    "Gewerblicher Rechtsschutz",
    "Handels- und Gesellschaftsrecht",
    "Urheber- und Medienrecht",
    "Informationstechnologierecht",
    "Bank- und Kapitalmarktrecht",
    "Agrarrecht",
    "Internationales Wirtschaftsrecht",
    "Vergaberecht",
    "Migrationsrecht",
    "Sportrecht",
  ];

  return [
    { id: "", label: "Titel entfernen" },
    ...specialistLawyerTitles.map((specialistLawyerTitle) => ({
      id: specialistLawyerTitle,
      label: specialistLawyerTitle,
    })),
  ];
};
